import React, {useContext, useEffect, useMemo, useState} from "react";
import "./App.css";
import {ActionContext, ActionType} from "./components/hooks/provider";
import Profile from "./views/profile";
import { Appointment, defaultAppointment, defaultPatientInfo, Language, Page, PatientInfo } from "./views/types"
import ConsentForms from "./views/consentForms";
import Navbar from "./components/navbar/navbar";
import Div100vh from "react-div-100vh";
import { getCheckInStatus, getOfficeLocationInfo, getPatientInfo } from './views/service'
import ContactInfo from "./views/contactInfo";
import BirthdayPage from "./views/birthdayPage";
import VerifyFailed from "./views/verifyFailed";
import LoadingModal from "./components/form/loadingModal";
import {useParams, useSearchParams} from "react-router-dom";
import Err from "./views/err";
import {AppConfig} from "./appConf";
import {mPrint} from "./utils/mPrint";
import BridgeHelper, { BridgePassProps, openInApp } from "./bridge/bridgeHelper";
import { updatePatientInfoWithPatientInApp } from "./bridge/appHelper";
import {PromptContext, MPrompt} from "./utils/promptContext"
import CheckIn from './views/checkIn'

export interface Window {
    passProps: BridgePassProps | undefined,
    location: Location,
}

declare global {
    var passProps: BridgePassProps | undefined
}

mPrint(`${AppConfig.buildDate} - ${AppConfig.envType} - ${openInApp}`);
if (openInApp) {
    mPrint(window.passProps)
}
sessionStorage.removeItem("spinnerKeys")

function App() {
    const [patientInfo, setPatientInfo] = useState<PatientInfo>(defaultPatientInfo)
    const [appointment, setAppointment] = useState<Appointment>(defaultAppointment)
    const {actionState, dispatchActionState} = useContext(ActionContext)
    const [loading, setLoading] = useState(false)
    const [invalid, setInvalid] = useState(false)

    const promptContextValue: MPrompt = {
        showSpinner: (key) => {
            if (openInApp) {
                BridgeHelper.showSpinner(key)
            } else {
                let spinnerKeys = sessionStorage.getItem("spinnerKeys") || ""
                let newKey = `${key || "officePal"}***`
                if (!spinnerKeys.includes(newKey)) {
                    spinnerKeys += newKey
                    sessionStorage.setItem("spinnerKeys", spinnerKeys)
                }
                setLoading(true)
            }
        },
        hideSpinner: (key) => {
            if (openInApp) {
                BridgeHelper.hideSpinner(key)
            } else {
                let spinnerKeys = sessionStorage.getItem("spinnerKeys") || ""
                let newKey = `${key || "officePal"}***`
                if (spinnerKeys.includes(newKey)) {
                    spinnerKeys = spinnerKeys.replace(newKey, "")
                    sessionStorage.setItem("spinnerKeys", spinnerKeys)
                }
                if (spinnerKeys === "") {
                    setLoading(false)
                }
            }
        },
        showInfo: () => {
            //
        },
        showSuccess: () => {
            //
        },
        showError: () => {
            //
        },
        showAlert: () => {
            //
        },
    }

    useEffect(() => {
        if (openInApp && navigator.userAgent) {
            try {
                const userAgent = JSON.parse(navigator.userAgent)
                let language = Language.en
                if (userAgent.appLanguage === "Chinese") {
                    language = Language.zh
                } else if (userAgent.appLanguage === "Spanish") {
                    language = Language.sp
                }
                mPrint(language)
                dispatchActionState({type: ActionType.language, value: language})
            } catch (e) {
                mPrint(e)
            }
        }
    }, [navigator.userAgent])

    const {token} = useParams()
    const [searchParams] = useSearchParams();
    mPrint(searchParams.get("redirecturl"));

    const redirecturl = searchParams.get("redirecturl")
    let pageFromUrl = Page.unknown
    const _page = searchParams.get("page")
    if (_page) {
        pageFromUrl = parseInt(_page, 10)
        if (!Page[pageFromUrl]) {
            pageFromUrl = Page.unknown
        }
    }
    useEffect(() => {
        if (!token) {
            alert("Link is required")
            setInvalid(true)
            return
        }
        getCheckInStatus(token, (checkedIn) => {
            if (checkedIn) {
                alert("Successfully Checked In")
            } else {
                requestPatientInfo(token)
            }
        })
    }, [token])

    const navigatePage = (page: Page) => {
        mPrint("page: ", page, pageFromUrl)
        dispatchActionState({type: ActionType.page, value: pageFromUrl ? pageFromUrl : page})
    }

    const requestPatientInfo = (token: string) => {
        promptContextValue.showSpinner("getPatientInfo")
        getPatientInfo(token, (isSuccess, info, appt, msg) => {
            promptContextValue.hideSpinner("getPatientInfo")
            if (isSuccess) {
                mPrint("PatientInfo: ", info)
                if (info) {
                    if (info.patientID > 0) {
                        // dispatchActionState({type: ActionType.page, value: Page.contact})
                        navigatePage(Page.contact)
                    } else {
                        // dispatchActionState({type: ActionType.page, value: Page.profile})
                        navigatePage(Page.profile)
                        if (openInApp && window?.passProps?.patient) {
                            const patient = window.passProps.patient
                            info = updatePatientInfoWithPatientInApp(info, patient)
                        }
                    }
                    setPatientInfo({...info, tokenID: token})
                    if (appt) {
                        requestOfficeLocationInfo(appt)
                    }
                } else {
                    alert("Get patient info failed")
                    setInvalid(true)
                }
            } else {
                alert(msg)
                setInvalid(true)
            }
        })
    }

    const requestOfficeLocationInfo = (appt: Appointment) => {
        getOfficeLocationInfo(appt.locationID, (data) => {
            const {address, phone, locationName, city, state, zip} = data
            setAppointment({
                ...appt,
                clinicName: locationName,
                clinicPhone: phone,
                isOfficeVisit: true,
                address: `${address} ${city} ${state}, ${zip}`,
            })
        })
    }

    const onSavePatientInfo = (info: PatientInfo) => {
        setPatientInfo(info)
    }

    const $content = useMemo(() => {
        switch (actionState.page) {
        case Page.profile:
            return <Profile patientInfo={patientInfo} onSavePatientInfo={onSavePatientInfo} />
        case Page.contact:
            return <ContactInfo patientInfo={patientInfo} />
        case Page.birthday:
            return <BirthdayPage patientInfo={patientInfo} />
        case Page.consentForm:
            return <ConsentForms patientInfo={patientInfo} appointment={appointment} />
        case Page.verifyFailed:
            return <VerifyFailed lastPage={actionState.lastPage} patientInfo={patientInfo} appointment={appointment} />
        case Page.checkIn:
            if (redirecturl !== null) {
                window.location.href = redirecturl
                return <div className={"w-full h-full"} />
            }
            return <CheckIn appointment={appointment} />
        default:
            return null
        }
    }, [actionState.page, patientInfo])

    const $loading = (
        <LoadingModal show={loading} />
    )

    const $errView = (
        <Err />
    )

    return (
        <PromptContext.Provider value={promptContextValue}>
            <Div100vh>
                <div className={"w-full flex flex-col items-center"}>
                    {openInApp ? null : <Navbar />}
                    <div className={"container flex flex-col items-center"}>
                        {invalid ? $errView : $content}
                    </div>
                </div>
                {$loading}
            </Div100vh>
        </PromptContext.Provider>
    );
}

export default App;
